import _ from 'lodash';
import app from '$$app';
import moment from 'moment';
import constants from '$$helpers/constants';
import timeTrackingModel from '$$timeTrackingModel';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import chartsConfig from './configCharts';
import reportHelpers from '../include/helpers';
import { reportWorkerError } from '$$workerErrorReporting';
import globalToast from '$$vueCmp/toast';
import chartsHelpers from '../include/charts/helpers';

const MONTH_IDS = ['jan', 'feb', 'march', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const MONTH_LOCALES = [
  'report_month_jan',
  'report_month_feb',
  'report_month_march',
  'report_month_apr',
  'report_month_may',
  'report_month_jun',
  'report_month_jul',
  'report_month_aug',
  'report_month_sep',
  'report_month_oct',
  'report_month_nov',
  'report_month_dec',
];

class TimeReport {
  constructor() {
    this.timeWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
    this.totalRange = {
      start: null,
      end: null,
    };
    this.baselineTotalRange = {
      start: null,
      end: null,
    };

    this.projectId = null;
    this.workerSettings = {};
  }

  init() {
    this.initWorkerSettings();
    this.initTimeWorker();
  }

  destroy() {
    
  }

  initWorkerSettings() {
    const allResourcesData = Store.getters['resourcesModel/getAllResources'];
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.workerSettings.projectConfigs = reportHelpers.prepareProjectConfigs();
    this.workerSettings.projectSettings = projectSettingsMap;
    this.workerSettings.customDaysData = customDayTemplatesModel.serialize();
    this.workerSettings.resourcesData = allResourcesData;
  }

  initTimeWorker() {
    if (window.Worker && !this.timeWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.timeWorker = new Worker('/workers/reports_time_worker.js' + `?${version}`);
      this._initEvents();
    } 
  }

  _initEvents() {
    const that = this;

    this.timeWorker.onmessage = function (e) {
      that.calculateMonthTimeLogReport(e.data);
    };

    this.timeWorker.onerror = function (e) {
      reportWorkerError(e, 'reports_time_worker');
      globalToast.error(__('time_calculating_error'));
    };
  }

  calculateTime(tasks, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const defaultRange = reportHelpers.getDefaultRange().last_6_months;

    const totalEstimate = tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);

    let tasksAndMilestones = tasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    let baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    tasksAndMilestones = reportHelpers.prepareTaskResourcesForWorker(tasksAndMilestones);
    baselineTasksAndMilestones = reportHelpers.prepareTaskResourcesForWorker(baselineTasksAndMilestones);

    const range = {
      start: currentFilter?.properties?.rangeDate?.start || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || totalEstimate?.end_date || defaultRange.end,
    };

    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || totalEstimate?.end_date || defaultRange.end,
    };

    this.totalRange = { ...range };
    this.baselineTotalRange = { ...baselineRange };
    this.projectId = totalEstimate.gantt_id;

    const filteredTasks = this.returnTasksByCurrentFilter(tasksAndMilestones, currentFilter);
    const filteredBaselineTasks = this.returnTasksByCurrentFilter(baselineTasksAndMilestones, currentFilter);
    const tasksByMonth = this.prepareMonthTasks(filteredTasks, filteredBaselineTasks, range);

    const preparedWorkerTasks = {
      allFilteredTasks: { tasks: filteredTasks, range },
      allFilteredBaselineTasks: { tasks: filteredBaselineTasks, range: baselineRange },
      ...tasksByMonth,
    };

    Object.entries(preparedWorkerTasks).forEach(([key, data]) => {
      this.reportTasks[key] = data.tasks;
    });
    this.sendDataToWorker(preparedWorkerTasks);
  }

  prepareMonthTasks(tasks, baselineTasks, range) {
    const startDate = moment(range.start);
    const endDate = moment(range.end);
    const startMonth = startDate.clone().startOf('month');
    const endMonth = endDate.clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const preparedTasksMap = {};

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(range.start).add(i, 'months');
      let firstDayOfCurrentMonth = currentMonth.clone().startOf('month');
      let lastDayOfCurrentMonth = currentMonth.clone().endOf('month');

      if (i === 0) {
        firstDayOfCurrentMonth = moment(range.start);
      }

      if (i === monthsDifference) {
        lastDayOfCurrentMonth = moment(range.end);
      }

      const tasksByMonth = tasks.filter(task => reportHelpers.returnTaskByRange(task, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));
      const baselineTasksByMonth = baselineTasks.filter(task => reportHelpers.returnTaskByRange(task, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));

      const id = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;

      const workerRange = {
        start: firstDayOfCurrentMonth.valueOf(),
        end: lastDayOfCurrentMonth.valueOf(),
      };

      preparedTasksMap[id] = { tasks: tasksByMonth, range: workerRange };
      preparedTasksMap[`${id}-baseline`] = { tasks: baselineTasksByMonth, range: workerRange };
    }

    return preparedTasksMap;
  }

  sendDataToWorker(tasksData) {
    this.timeWorker.postMessage(_.cloneDeep({
      projectConfigs: this.workerSettings.projectConfigs,
      projectSettings: this.workerSettings.projectSettings,
      customDaysData: this.workerSettings.customDaysData,
      resourcesData: this.workerSettings.resourcesData,
      tasksData,
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange(task, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resources.length) {
        return task;
      }

      const assigneeTask = task.resources.find(res => currentFilter.properties.assignee.includes(+res.resource_id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  calculateMonthTimeLogReport(data) {
    const tableData = [];

    const isActualToBaseline = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE;
    const isActualToPlanned = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
    const isPlannedToBaseline = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE

    let cumulativeEstimationResult = 0;
    let baselineCumulativeEstimationResult = 0;
    let cumulativeLogsResult = 0;

    const reportTasks = data || this.reportTasks;

    if (!reportTasks.allFilteredTasks?.length) {
      app.trigger('onAfterMonthTimeReportCalculation', null);

      return;
    }

    const xAxisValues = [];

    const startMonth = moment(this.totalRange.start).clone().startOf('month');
    const endMonth = moment(this.totalRange.end).clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const resultConfig = _.cloneDeep(chartsConfig.monthReport(this.calculationMode).default);

    const projectTaskslogsData = timeTrackingModel.getLogsByParams(this.projectId);

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(this.totalRange.start).add(i, 'months');
      const startOfMonth = currentMonth.clone().startOf('month');
      const endOfMonth = currentMonth.clone().endOf('month');
      const dateString = `${MONTH_LOCALES[currentMonth.month()]} ${currentMonth.year()}`;
      const dateKey = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;
      const baselineDateKey = `${dateKey}-baseline`;

      const monthLabel = dateString.split(' ');
      const tableMonthLabel = `${__(`${monthLabel[0]}_full`)} ${monthLabel[1]}`;

      xAxisValues.push(dateString);

      if (isPlannedToBaseline) {
        const tasksByMonth = reportTasks[dateKey];
        const baselineTasksByMonth = reportTasks[baselineDateKey];

        const currentMonthEstimation = tasksByMonth.reduce((sum, task) => sum + +task.estimation, 0);
        const baselineCurrentMonthEstimation = baselineTasksByMonth.reduce((sum, task) => sum + +task.estimation, 0);

        cumulativeEstimationResult += currentMonthEstimation;
        baselineCumulativeEstimationResult += baselineCurrentMonthEstimation;

        resultConfig.series = resultConfig.series.filter(item => !['logs', 'cumulativeLogs'].includes(item.name)).map(item => {
          if (item.name === 'estimation') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthEstimation, 
              hasLabel: true,
              type: 'time'
            })
          }

          if (item.name === 'baselineEstimation') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: baselineCurrentMonthEstimation, 
              hasLabel: true,
              type: 'time'
            })
          }

          if (item.name === 'cumulativeEstimation') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeEstimationResult, 
              hasLabel: false,
              type: 'time'
            })
          }

          if (item.name === 'cumulativeBaselineEstimation') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: baselineCumulativeEstimationResult, 
              hasLabel: false,
              type: 'time'
            })
          }

          return item;
        });

        tableData.push({
          month: tableMonthLabel,
          estimation: currentMonthEstimation.toFixed(2),
          baseline_estimation: baselineCurrentMonthEstimation.toFixed(2),
          logs: 0,
          variance: (baselineCurrentMonthEstimation - currentMonthEstimation).toFixed(2),
          cum_estimation: cumulativeEstimationResult.toFixed(2),
          cum_baseline_estimation: baselineCumulativeEstimationResult.toFixed(2),
          cum_logs: 0,
          cum_variance: (baselineCumulativeEstimationResult - cumulativeEstimationResult).toFixed(2),
        });
      }

      if (isActualToPlanned || isActualToBaseline) {
        const tasksByMonth = isActualToPlanned ? reportTasks[dateKey] : reportTasks[baselineDateKey]
        let currentMonthLogsValue = 0;

        tasksByMonth.forEach(task => {
          // const logsData = timeTrackingModel.getLogsByParams(task.gantt_id, task.id);
          const logsData = projectTaskslogsData?.tasks[task.id];

          if (logsData?.logs) {
            const sum = logsData.logs.filter(log => {
              const logDate = moment(log.date);

              if (logDate.isBefore(startOfMonth)) {
                return false;
              }

              if (logDate.isAfter(endOfMonth)) {
                return false;
              }

              return true;
            }).reduce((sum, log) => sum + log.time, 0);

            currentMonthLogsValue += isNaN(sum) ? 0 : +(sum / 60).toFixed(2);
            // const sum = logsData.logs.reduce((sum, log) => sum + log.time, 0);

            // currentMonthLogsValue = +(sum / 60).toFixed(2) || 0;
          }
        });

        const currentMonthEstimation = tasksByMonth.reduce((sum, task) => sum + +task.estimation, 0);

        cumulativeEstimationResult += currentMonthEstimation;
        cumulativeLogsResult += currentMonthLogsValue;

        let seriesItems = resultConfig.series.filter(item => !['baselineEstimation', 'cumulativeBaselineEstimation'].includes(item.name));

        if (isActualToBaseline) {
          seriesItems = resultConfig.series.filter(item => !['estimation', 'cumulativeEstimation'].includes(item.name));
        }

        resultConfig.series = seriesItems.map(item => {
          if (item.name === 'estimation' || item.name === 'baselineEstimation') {
            if (isActualToBaseline && item.name === 'baselineEstimation') {
              return chartsHelpers.prepareChartItemWithCalculatedValue({
                item, 
                value: currentMonthEstimation, 
                hasLabel: true, 
                color: '#8BC34A',
                type: 'time'
              })
            }

            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthEstimation, 
              hasLabel: true,
              type: 'time'
            })
          }

          if (item.name === 'logs') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthLogsValue, 
              hasLabel: true,
              type: 'time'
            })
          }

          if (item.name === 'cumulativeEstimation' || item.name === 'cumulativeBaselineEstimation') {
            if (isActualToBaseline && item.name === 'cumulativeBaselineEstimation') {
              return chartsHelpers.prepareChartItemWithCalculatedValue({
                item, 
                value: cumulativeEstimationResult, 
                hasLabel: false, 
                color: '#6E8F3F',
                type: 'time'
              })
            }

            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeEstimationResult, 
              hasLabel: false,
              type: 'time'
            })
          }

          if (item.name === 'cumulativeLogs') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeLogsResult, 
              hasLabel: false,
              type: 'time'
            })
          }

          return item;
        });

        tableData.push({
          month: tableMonthLabel,
          estimation: currentMonthEstimation.toFixed(2),
          baseline_estimation: currentMonthEstimation.toFixed(2),
          logs: currentMonthLogsValue,
          variance: (currentMonthEstimation - currentMonthLogsValue).toFixed(2),
          cum_estimation: cumulativeEstimationResult.toFixed(2),
          cum_baseline_estimation: cumulativeEstimationResult.toFixed(2),
          cum_logs: cumulativeLogsResult,
          cum_variance: (cumulativeEstimationResult - cumulativeLogsResult).toFixed(2),
        });
      }
    }

    const barValues = resultConfig.series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
    const lineValues = resultConfig.series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
    const intervals = chartsHelpers.calculateYAxisIntervals(barValues, lineValues);

    if (intervals.left > 0 && intervals.right > 0) {
      resultConfig.xAxis[0].data = xAxisValues;
      resultConfig.yAxis[0].interval = intervals.left;
      resultConfig.yAxis[1].interval = intervals.right;

      app.trigger('onAfterMonthTimeReportCalculation', {
        chart: resultConfig,
        table: tableData,
      });

      return;
    }

    app.trigger('onAfterMonthTimeReportCalculation', null);
  }
}

const timeReport = new TimeReport();

export default timeReport;
